import React from "react";
import DataTable from "components/DataTable/DataTable";
import ApplicationSearchTableLoading from "./ApplicationSearchTableLoading";
import { css } from "@emotion/react";
import { styled } from "@mui/material/styles";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import { useApplicationSearchColumns } from "./functions/useApplicationSearchColumns";
import { IApplicationSearchItem } from "models/Application.model";

type Props = {
  data: IApplicationSearchItem[];
  loadNextPage: () => void;
  hasNextPage?: boolean;
  isLoading?: boolean;
  onClickOpenApplication: (applicationId: number) => void;
  onClickRequestAccess: (applicationId: number) => void;
};

const ApplicationSearchTable: React.FC<Props> = ({
  data,
  hasNextPage = false,
  isLoading = false,
  loadNextPage,
  onClickOpenApplication,
  onClickRequestAccess,
}) => {
  const columns = useApplicationSearchColumns({ onClickOpenApplication, onClickRequestAccess });
  const tableRef = React.useRef<HTMLDivElement>(null);

  const handleScrollPositionChange = React.useCallback(() => {
    if (!hasNextPage) {
      return;
    }

    loadNextPage();
  }, [hasNextPage, loadNextPage]);

  useInfiniteScroll({
    scrollElement: tableRef.current,
    onScrollLimitReached: handleScrollPositionChange,
  });

  return (
    <StyledDataTable
      ref={tableRef}
      data-testid={"ApplicationSearchTable"}
      data={data}
      sortById={"applicationNumber"}
      tableLayout={"fixed"}
      verticalAlign={"top"}
      columns={columns}
      isLoading={isLoading}
      loadingComponent={<ApplicationSearchTableLoading />}
    />
  );
};

const StyledDataTable = styled(DataTable)(
  ({ theme }) => css`
    border: 1px solid ${theme.palette.grey[300]};

    table > thead > tr > th {
      border-bottom: 1px solid ${theme.palette.grey[500]};
      border-right: 0;
      color: ${theme.palette.text.secondary};
    }

    table > tbody > tr:first-child > td {
      border-top: 1px solid ${theme.palette.grey[300]};
    }

    table > tbody > tr > td {
      border-bottom: 1px solid ${theme.palette.grey[300]};
      border-right: 0;
      vertical-align: middle;
      padding: 16px;

      &:not(:last-child) {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }
  `
);

export default ApplicationSearchTable;
